<template>
  <div class="m-page">
    <div class="filters">
      <van-dropdown-menu>
        <van-dropdown-item v-model="sample" :options="sampleOptions" @change="loadData" />
        <van-dropdown-item v-model="curWindow" :options="windowOptions" />
        <van-dropdown-item v-model="direction" :options="directionOptions" />
      </van-dropdown-menu>
      <div class="filters-row">
        <van-button size="small" type="info" :loading="loading" icon="replay" @click="loadData">刷新</van-button>
        <span class="tip">{{ latestDate || '—' }} · {{ rankedRows.length }} 只</span>
      </div>
    </div>

    <van-empty v-if="error" :description="error" />
    <van-loading v-else-if="loading && !items.length" class="page-loading" vertical>计算中...</van-loading>
    <van-empty v-else-if="queried && !rankedRows.length" description="当前区间无可用数据" />

    <van-list v-else>
      <div v-for="row in displayRows" :key="row.code" class="card">
        <div class="card-head">
          <span class="rank">#{{ row.rank }}</span>
          <a class="code" :href="stockstarCodeLink(row.code)" target="_blank" rel="noopener noreferrer">{{ row.code }}</a>
          <a class="name" :href="eastmoneyCodeLink(row)" target="_blank" rel="noopener noreferrer">{{ row.name }}</a>
          <span class="rps">RPS {{ row.rps }}</span>
        </div>
        <div class="card-meta">
          <span>{{ row.industry || '—' }}</span>
          <span>{{ formatPrice(row.close) }}</span>
          <span :class="row.pcts[curWindow] >= 0 ? 'up' : 'down'">
            {{ curWindow }}日 {{ formatPct(row.pcts[curWindow]) }}%
          </span>
        </div>
        <div class="pct-grid">
          <span
            v-for="w in previewWindows"
            :key="w"
            :class="['pct-item', row.pcts[w] == null ? 'muted' : (row.pcts[w] >= 0 ? 'up' : 'down')]"
          >
            {{ w }}日 {{ row.pcts[w] == null ? '—' : formatPct(row.pcts[w]) }}%
          </span>
        </div>
      </div>

      <div v-if="rankedRows.length > pageSize" class="pager">
        <van-pagination
          v-model="page"
          :total-items="rankedRows.length"
          :items-per-page="pageSize"
          force-ellipses
        />
      </div>
    </van-list>
  </div>
</template>

<script>
import { fetchWithCredentials } from '../utils/http.js'
import { eastmoneyCodeLink, stockstarCodeLink } from '../utils/stockLink.js'

const WINDOWS = [1, 3, 5, 10, 20, 30, 60, 120, 250]

export default {
  name: 'MobileRadar',
  data() {
    return {
      sample: 'top500',
      curWindow: 5,
      direction: 'asc',
      items: [],
      counts: {},
      latestDate: '',
      loading: false,
      queried: false,
      error: '',
      page: 1,
      pageSize: 50,
    }
  },
  computed: {
    sampleOptions() {
      const labels = { all: '全市场', top500: 'Top500', p60: 'P60宽松', p75: 'P75严格' }
      return ['all', 'top500', 'p60', 'p75'].map((value) => {
        const n = this.counts[value]
        return {
          text: n != null ? `${labels[value]} ${n}` : labels[value],
          value,
        }
      })
    },
    windowOptions() {
      return WINDOWS.map((w) => ({ text: `${w}日`, value: w }))
    },
    directionOptions() {
      return [
        { text: '跌幅榜', value: 'asc' },
        { text: '涨幅榜', value: 'desc' },
      ]
    },
    rankedRows() {
      const w = this.curWindow
      const valid = this.items.filter((it) => it.pcts[w] != null && !it.exr.includes(w))
      const desc = this.direction === 'desc'
      valid.sort((a, b) => (desc ? b.pcts[w] - a.pcts[w] : a.pcts[w] - b.pcts[w]))
      const n = valid.length
      return valid.map((it, i) => ({
        ...it,
        rank: i + 1,
        rps: n > 1 ? Math.round((100 * (n - 1 - i)) / (n - 1)) : 100,
      }))
    },
    displayRows() {
      const start = (this.page - 1) * this.pageSize
      return this.rankedRows.slice(start, start + this.pageSize)
    },
    previewWindows() {
      return [1, 20, 60, 250].filter((w) => w !== this.curWindow)
    },
  },
  watch: {
    curWindow() { this.page = 1 },
    direction() { this.page = 1 },
    sample() { this.page = 1 },
  },
  async created() {
    await this.loadData()
    await this.loadCounts()
  },
  methods: {
    eastmoneyCodeLink,
    stockstarCodeLink,
    async loadData() {
      this.loading = true
      this.error = ''
      this.queried = true
      try {
        const { res, data } = await fetchWithCredentials(`/api/window-ranking?sample=${this.sample}`)
        if (!res.ok) throw new Error(data.error || `波段雷达接口失败 ${res.status}`)
        this.latestDate = data.latestDate || ''
        this.items = data.items || []
      } catch (e) {
        this.items = []
        this.error = e.message || String(e)
      } finally {
        this.loading = false
      }
    },
    async loadCounts() {
      try {
        const results = await Promise.all(
          ['all', 'top500', 'p60', 'p75'].map((s) =>
            fetchWithCredentials(`/api/window-ranking?sample=${s}`).then(({ res, data }) => ({
              s,
              n: res.ok ? data.count ?? (data.items || []).length : null,
            }))
          )
        )
        const counts = {}
        results.forEach(({ s, n }) => { counts[s] = n })
        this.counts = counts
      } catch {
        this.counts = { [this.sample]: this.items.length }
      }
    },
    formatPrice(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : '—'
    },
    formatPct(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : '—'
    },
  },
}
</script>

<style scoped>
.m-page { padding-bottom: 12px; }
.filters-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
}
.tip { font-size: 12px; color: #969799; }
.page-loading { padding: 48px 0; }
.card {
  margin: 8px 12px;
  padding: 12px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.card-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.rank { font-size: 12px; color: #969799; min-width: 28px; }
.code, .name { color: #1989fa; text-decoration: none; font-size: 14px; }
.name {
  font-weight: 600;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rps { font-size: 12px; color: #323233; font-weight: 600; }
.card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-top: 6px;
  font-size: 12px;
  color: #646566;
}
.pct-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 8px;
  margin-top: 8px;
  font-size: 12px;
}
.pct-item { color: #646566; }
.muted { color: #c8c9cc; }
.up { color: #ee0a24; }
.down { color: #07c160; }
.pager { padding: 12px 8px 4px; }
</style>
